import Config from "Config";
import { getCookie } from "helpers/cookie";
import { getFromLocalStorage } from "helpers/localstorage";
import { TranslationKeys } from "locale/localizationContext";
import { useLanguage } from "locale/useLanguage";
import { RoleEnum } from "models/role.types";
import { config as statsConfig } from "pages/stats/config";
import { useMemo } from "react";
import { routes } from "routes/routes";
import { NavIconName } from "./icons";
import useCachedFamilies from "./useCachedFamilies";

export type NavChild = { path: string; title: string };

export type NavItem = {
  id: string;
  path: string;
  title: string;
  icon: NavIconName;
  /** Sottopagine: aperte nel menu se `expandable`, altrimenti solo nella ricerca */
  children?: NavChild[];
  expandable?: boolean;
  /** Percorsi per cui la voce risulta attiva */
  match: string[];
};

export type NavGroup = { id: string; title: string; items: NavItem[] };

// Voce di menu: `section` e' la chiave dei permessi in Config.roleScopes, `routeId` la rotta da cui prendere percorso e titolo.
type ItemDef = {
  id: string;
  section: string;
  routeId: string;
  icon: NavIconName;
  path?: string;
  expandable?: boolean;
  match?: string[];
};

const groupDefs: { id: "rental" | "analysis" | "backoffice"; items: ItemDef[] }[] = [
  {
    id: "rental",
    items: [
      { id: "home", section: "products", routeId: "shopHome", icon: "home" },
      {
        id: "products",
        section: "products",
        routeId: "products",
        icon: "products",
        expandable: true,
        match: ["/products", "/bundles", "/cart"],
      },
      { id: "orders", section: "orders", routeId: "orders", icon: "orders" },
      { id: "contracts", section: "contracts", routeId: "contracts", icon: "contracts" },
      { id: "tickets", section: "tickets", routeId: "tickets", icon: "tickets" },
    ],
  },
  {
    id: "analysis",
    items: [
      { id: "stats", section: "stats", routeId: "stats", icon: "stats", path: "/stats" },
      { id: "commissions", section: "commissions", routeId: "commissions", icon: "commissions" },
    ],
  },
  {
    id: "backoffice",
    items: [
      { id: "customers", section: "customers", routeId: "customers", icon: "customers" },
      {
        id: "anagManagement",
        section: "anagManagement",
        routeId: "anagManagement",
        icon: "registry",
        expandable: true,
      },
      { id: "users", section: "users", routeId: "users", icon: "users" },
      { id: "emails", section: "users", routeId: "emails", icon: "emails" },
      { id: "chatLogs", section: "chatLogs", routeId: "chatLogs", icon: "logs" },
      { id: "importexcel", section: "importexcel", routeId: "importexcel", icon: "setup" },
    ],
  },
];

// Stessa regola di App.tsx: le provvigioni si vedono solo se l'agenzia emette fattura (o per gli admin).
export const getPermittedSections = (): string[] => {
  const role = getCookie("role");
  const { emetteFattura } = (getFromLocalStorage() as any) || {};
  const roleKey = Object.entries(RoleEnum).find(([, value]) => value === role)?.[0];
  const scopes = { ...((Config.roleScopes as any)[roleKey ?? ""] ?? {}) };
  if (!emetteFattura && role !== RoleEnum.SUPERADMINISTRATOR && role !== RoleEnum.ADMINISTRATOR) {
    delete scopes.commissions;
  }
  return Object.keys(scopes);
};

export const isActivePath = (pathname: string, path: string) =>
  path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(`${path}/`);

function useNavigation() {
  const [t] = useLanguage();
  const { families } = useCachedFamilies();
  const role = getCookie("role");

  const groups = useMemo<NavGroup[]>(() => {
    const permitted = getPermittedSections();
    const tr = (key: string) => t(key as TranslationKeys<string>);

    const childrenOf = (id: string): NavChild[] | undefined => {
      if (id === "products") {
        return [
          { path: "/products", title: tr("navigation.catalog") },
          { path: "/bundles", title: tr("pages.bundles.title") },
        ];
      }
      if (id === "stats") {
        return Object.keys(statsConfig).map((key) => ({
          path: `/stats/${key}`,
          title: tr(`pages.stats.stats_type.${key}.title`),
        }));
      }
      if (id === "anagManagement") {
        return routes
          .filter((r) => r.section === "anagManagement" && !r.showInMenu && !r.hideInSubMenu)
          .map((r) => ({ path: r.path, title: tr(r.titleLocalizationKey) }));
      }
      return undefined;
    };

    return groupDefs
      .map((group) => ({
        id: group.id,
        title: tr(`navigation.groups.${group.id}`),
        items: group.items
          .filter((item) => permitted.includes(item.section))
          .map((item) => {
            const route = routes.find((r) => r.id === item.routeId);
            if (!route) return null;
            const path = item.path ?? route.path;
            const children = childrenOf(item.id);
            return {
              id: item.id,
              path,
              title: tr(route.titleLocalizationKey),
              icon: item.icon,
              children,
              expandable: item.expandable,
              match: item.match ?? [path, ...(children ?? []).map((c) => c.path)],
            } as NavItem;
          })
          .filter((item): item is NavItem => item !== null),
      }))
      .filter((group) => group.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, t]);

  return { groups, families };
}

export default useNavigation;
